import React, { useEffect, useState } from 'react';
import { useToast } from 'core/toast';
import { convertCurrencyString, normalizeCurrencyString } from 'core/utilities';
import { 
    getWaitlistPosition,
    updateApplication, 
    upsertTargetMortgageProperty, 
    upsertTargetMortgagePropertyDetails 
} from 'core/api';
import { 
    MultistepForm, 
    SetIsLoading, 
    SetSubmitError 
} from 'view/components/common';
import { StepProps } from '../../MortgageApplicationPage';
import { formSteps } from './formSteps';
import { ApplicationType } from 'view/pages';

type FormValues = {
    mortgage_type? : number;

    // New mortgage
    full_address : string;
    address : string;
    suite : string;
    city : string;
    region : string;
    country : string;
    postal : string;
    offer_made : boolean;
    purchase_price : string;
    downpayment_amount : string;
    gift : boolean;
    closing_date : string;

    // Transfer
    mortgage_value : string;
    mortgage_left : string;
    mortgage_payments : string;
    mortgage_tax : string;
    mortgage_maintenance : string;
};

export const Mortgage : React.FunctionComponent<StepProps> = ({
    step, 
    setStep,
    progressForward,
    progressBackward,
    mortgageType,
    application,
    complete,
    refresh
}) => 
{
    const { setToastMessage } = useToast();
    const onExit = () =>
    {
        setStep(s => s - 1);
        progressBackward();
    };

    const [ initialValues, setInitialValues ] = useState<FormValues>({
        full_address : '',
        address : '',
        suite : '',
        city : '',
        region : '',
        country : '',
        postal : '',
        offer_made : false,
        purchase_price : '',
        downpayment_amount : '',
        gift : false,
        closing_date : '',
        mortgage_value : '',
        mortgage_left : '',
        mortgage_payments : '',
        mortgage_tax : '',
        mortgage_maintenance : '',
    });

    const { 
        address : mortgageAddress,
        downpayments
    } = complete;

    useEffect(() => 
    {
        if (mortgageAddress)
        {
            const { 
                address, 
                suite, 
                city, 
                region, 
                country, 
                postal,
                purchase_price,
                closing_date
            } = mortgageAddress;

            const date = closing_date ? closing_date.split('-').join('/') : '';

            setInitialValues(v => ({
                ...v,
                purchase_price : normalizeCurrencyString(purchase_price),
                closing_date : date,
                full_address : address,
                address,
                suite,
                city, 
                region,
                country,
                postal
            }));
        }

        if (downpayments && downpayments.length)
        {
            const { 
                gift,
                downpayment_amount 
            } = downpayments[0];

            setInitialValues(v => ({
                ...v,
                downpayment_amount : normalizeCurrencyString(downpayment_amount),
                gift
            }));
        }
    }, [ setInitialValues ]);

    const onSubmit = (_: SetSubmitError, setIsLoading : SetIsLoading) =>
    {
        return async (payload : FormValues) => 
        {
            const {
                mortgage_value,
                mortgage_left,
                mortgage_payments,
                mortgage_tax,
                mortgage_maintenance
            } = payload;

            setIsLoading(true);

            const updateReply = await updateApplication(application.id, {
                estimated_property_value: convertCurrencyString(mortgage_value),
                remaining_mortgage: convertCurrencyString(mortgage_left),
                monthly_mortgage_payments: convertCurrencyString(mortgage_payments),
                monthly_property_tax: convertCurrencyString(mortgage_tax),
                monthly_maintenance_fee: convertCurrencyString(mortgage_maintenance)
            });

            const waitlistReply = await getWaitlistPosition();
            const hasSucceeded = updateReply.success && waitlistReply.success;

            if (hasSucceeded)
            {
                setTimeout(() =>
                {
                    setStep(s => s + 1);
                    progressForward();
                }, 500);
            }
            else 
            {
                setIsLoading(false);
                setToastMessage();
            }
        };
    };

    const onNext = {
        // 1 : async (payload : FormValues, setIsLoading : SetIsLoading) =>
        // {
        //     const { mortgage_type } = payload;
        //     setIsLoading(true);

        //     if (mortgage_type === 0)
        //     {
        //         let hasSucceeded = true;
        //         const updateReply = await updateApplication(application.id, { _type : 'p' });
        //         hasSucceeded = hasSucceeded && updateReply.success;
        //         const waitlistReply = await getWaitlistPosition();
        //         hasSucceeded = hasSucceeded && waitlistReply.success;

        //         if (hasSucceeded)
        //         {
        //             setTimeout(() => setStep(s => s + 1), 500);
        //         }
        //         else
        //         {
        //             setIsLoading(false);
        //             setToastMessage();
        //             return 0;
        //         }
        //     }
        //     else if (mortgage_type === 1)
        //     {
        //         await updateApplication(application.id, { _type : 'n' });
        //         return 1;
        //     }
        //     else 
        //     {
        //         await updateApplication(application.id, { _type : 't' });
        //         return 3;
        //     }
        // },
        0 : async () => 
        {
            if (mortgageType === ApplicationType.NEW_PURCHASE) return 1;
            return 3;
        },
        1 : async (payload : FormValues, setIsLoading : SetIsLoading) =>
        {
            const {
                full_address,
                address,
                suite,
                city,
                region,
                country,
                postal,
                offer_made
            } = payload;

            setInitialValues(v => (({
                ...v,
                full_address,
                address,
                suite,
                city,
                region,
                country,
                postal,
                offer_made
            })));
            setIsLoading(true);

            const { success } = await upsertTargetMortgageProperty(application.id, {
                propertyId : complete?.address?.id ?? null,
                address,
                suite,
                city,
                region,
                country,
                postal
            });

            if (success)
            {
                refresh();
                return 1;
            }
            else 
            {
                setToastMessage();
                return 0;
            }
        },
        2 : async (payload : FormValues, setIsLoading : SetIsLoading) =>
        {
            const {
                purchase_price,
                downpayment_amount,
                gift,
                closing_date
            } = payload;

            const price = purchase_price ? parseInt(convertCurrencyString(purchase_price)) : 0;
            const downpayment = downpayment_amount ? parseInt(convertCurrencyString(downpayment_amount)) : 0;
            const monthly_maintenance = 0;
            const loan_amount = price - downpayment;

            setIsLoading(true);
            setInitialValues(v => ({
                ...v,
                purchase_price,
                downpayment_amount,
                gift,
                closing_date
            }));

            const { address, downpayments } = complete;
            const propertyId = address ? address.id : null;
            const downpaymentId = (downpayments && downpayments.length) ? downpayments[0].id : null; 

            const updateReply = await upsertTargetMortgagePropertyDetails(application.id, {
                application : {
                    loan_amount
                },
                downpayment : {
                    id : downpaymentId, 
                    data : {
                        gift,
                        amount : downpayment
                    }
                },
                property : {
                    id : propertyId,
                    data : {
                        closing_date : closing_date.split('/').join('-'),
                        purchase_price : price,
                        gift,
                        monthly_maintenance
                    }
                }
            });

            const waitlistReply = await getWaitlistPosition();
            const hasSucceeded = updateReply.success && waitlistReply.success;

            if (hasSucceeded)
            {
                const waitlistReply = await getWaitlistPosition();

                if (waitlistReply.success)
                {
                    setTimeout(() => setStep(s => s + 1), 500);
                }
                else 
                {
                    setIsLoading(false);
                    setToastMessage();
                    return 0;
                }
            }
            else 
            {
                setIsLoading(false);
                setToastMessage();
                return 0;
            }
        },
    };

    const onBack = {
        3 : () =>
        {
            return 3;
        },
    };

    return (
        <MultistepForm
            title='Mortgage Information'
            backText='Back'
            stepTitle={() => `Step ${step}`}
            onExit={onExit}
            initialValues={initialValues}
            formSteps={formSteps}
            onSubmit={onSubmit}
            onNext={onNext}
            onBack={onBack}
        />
    );
};